import { UserContext } from '@/context/UserContext';
import useI18n from '@/hooks/useI18n';
import { useContext } from 'react';
import ToolTip from '../ToolTip';
import type { PerformanceMetricsDeltas } from '@/hooks/queries/useAnalyticsQueries';
import { formatCount, formatPercent } from '@/utils/numberUtils';

type Variant = 'brand' | 'individual';

interface PerformanceMetricsShape {
  totalPosts?: number;
  totalImpressions?: number;
  totalLikes?: number;
  totalShares?: number;
  engagementRate?: number;
}

interface CampaignOverViewComponentProps {
  /** Brand-aggregate page payload — kept as the canonical prop for back-compat. */
  brandMetrics?: PerformanceMetricsShape | null;
  /** Individual-campaign page payload (same shape as brandMetrics). */
  metrics?: PerformanceMetricsShape | null;
  /** Day-over-day deltas — only consumed when variant === 'individual'. */
  deltas?: PerformanceMetricsDeltas | null;
  /** Visual variant. Brand = single wrapper card w/ icons + dividers. Individual = separate cards w/ delta chips, no icons. */
  variant?: Variant;
}

const DUMMY_METRICS: PerformanceMetricsShape = {
  totalImpressions: 327_000,
  totalPosts: 92,
  totalLikes: 18_300,
  totalShares: 1_210,
  engagementRate: 8,
};

// Demo day-over-day deltas (mirror the seeded yesterday snapshot: ~+6%).
// totalPosts = 0 → "No change" pill, matching the real-data path where the
// cashback count is unchanged day-over-day.
const DUMMY_DELTAS: PerformanceMetricsDeltas = {
  totalPosts: 0,
  totalViews: 6.2,
  totalLikes: 6.4,
  totalShares: -4.7,
  engagementRate: 6.7,
};

const InfoIcon = () => <span className="inline-block">ⓘ</span>;

interface DeltaChipProps {
  value: number | null | undefined;
}

const DeltaChip = ({ value }: DeltaChipProps) => {
  const { t } = useI18n();

  // `null`/`undefined` from the backend = "no baseline yet" (first view,
  // gap day, or zero baseline). Per spec we render nothing — emitting a
  // "No change" pill in this case would misrepresent "we don't know" as
  // "definitely flat". A zero delta with a real baseline DOES render the
  // "No change" pill.
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }

  const numeric = value as number;
  const isChange = numeric !== 0;

  // Per Figma: positive bg #E6F8F0 fg #00AE3E; negative bg #F8E6E6 fg red;
  // no-change bg #00000014 fg #9B919D. All chips: padding 4/8, radius 49,
  // Inter 12/16. Percent value is Medium (500); descriptor "from previous
  // day" is Regular (400). "No change" pill is single-span Medium 500.
  let bg = '#00000014';
  let fg = '#9B919D';

  if (isChange) {
    const isPositive = numeric > 0;
    bg = isPositive ? '#E6F8F0' : '#F8E6E6';
    fg = isPositive ? '#00AE3E' : '#B42318';
  }

  const baseStyle = {
    padding: '4px 7px',
    borderRadius: 49,
    backgroundColor: bg,
    color: fg,
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    lineHeight: '16px',
  } as const;

  if (!isChange) {
    return (
      <span
        className="inline-flex items-center justify-center w-fit"
        style={{ ...baseStyle, fontWeight: 500 }}
      >
        {String(t('common.noChange'))}
      </span>
    );
  }

  const sign = numeric > 0 ? '+' : '-';
  const absVal = Math.abs(numeric);
  const abs =
    absVal < 10
      ? String(parseFloat(absVal.toFixed(1)))
      : String(Math.round(absVal));
  return (
    <span
      className="inline-flex items-center justify-center w-fit"
      style={{ ...baseStyle, gap: 2 }}
    >
      <span style={{ fontWeight: 500 }}>{`${sign}${abs}%`}</span>
      <span style={{ fontWeight: 400 }}>
        {String(t('common.fromPreviousDay'))}
      </span>
    </span>
  );
};

interface StatDef {
  label: string;
  value: string;
  src: string;
  deltaKey: keyof PerformanceMetricsDeltas;
  tooltip?: string;
}

const CampaignOverViewComponent = ({
  brandMetrics,
  metrics,
  deltas,
  variant = 'brand',
}: CampaignOverViewComponentProps) => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { isDummyUser } = userContext;
  const source = metrics ?? brandMetrics;
  const resolved = isDummyUser ? DUMMY_METRICS : source;
  const resolvedDeltas = isDummyUser ? DUMMY_DELTAS : deltas;

  const stats: StatDef[] = [
    {
      label: String(t('common.totalPosts')),
      value: formatCount(resolved?.totalPosts ?? 0),
      src: '/assets/send.svg',
      deltaKey: 'totalPosts',
    },
    {
      label: String(t('common.totalViews')),
      value: formatCount(resolved?.totalImpressions ?? 0),
      src: '/assets/eye.svg',
      deltaKey: 'totalViews',
    },
    {
      label: String(t('common.totalLikes')),
      value: formatCount(resolved?.totalLikes ?? 0),
      src: '/assets/heart.svg',
      deltaKey: 'totalLikes',
    },
    {
      label: String(t('common.totalShares')),
      value: formatCount(resolved?.totalShares ?? 0),
      src: '/assets/share.svg',
      deltaKey: 'totalShares',
    },
    {
      label: String(t('common.engagementRate')),
      value: formatPercent(resolved?.engagementRate ?? 0),
      src: '/assets/user-black.svg',
      deltaKey: 'engagementRate',
      tooltip: String(t('common.EngagementRateTooltipDesc')),
    },
  ];

  const header = (
    <div className="flex items-center" style={{ gap: 4 }}>
      <h2
        className="font-medium opacity-90"
        style={{
          fontFamily: 'Manrope, sans-serif',
          fontSize: 14,
          lineHeight: '120%',
          color: '#121111',
        }}
      >
        {t('common.performanceOverview')}
      </h2>
    </div>
  );

  if (variant === 'individual') {
    return (
      <div className="flex flex-col" style={{ gap: 12 }}>
        {header}
        {/* 2-up on mobile, 3-up on small tablets, 5-up on desktop. Cards keep
            their fixed 128px height; the grid just controls how many sit on
            one row at each breakpoint. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
          {stats.map((stat, index) => (
            <div
              key={`card-${index}`}
              className="bg-white border border-[#E5E9EB] rounded-[24px] flex flex-col items-center justify-center"
              style={{
                minWidth: 0,
                height: 128,
                padding: 24,
                gap: 8,
                boxShadow: '0px 21px 94px 0px #00000008',
              }}
            >
              <div
                className="font-medium text-center"
                style={{
                  color: '#191B1C',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 24,
                  lineHeight: '32px',
                }}
              >
                {stat.value}
              </div>
              <div
                className="flex items-center justify-center"
                style={{
                  gap: 4,
                  color: '#9B919D',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 12,
                  lineHeight: '16px',
                  fontWeight: 400,
                }}
              >
                <span>{stat.label}</span>
                {stat.tooltip ? (
                  <ToolTip text={stat.tooltip} position="top">
                    <InfoIcon />
                  </ToolTip>
                ) : null}
              </div>
              <DeltaChip value={resolvedDeltas?.[stat.deltaKey]} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Brand variant — one wrapper card with 5 columns. Vertical dividers
  // between columns kick in only at `lg` (the columns flow into a 2-up /
  // 3-up grid on narrower viewports where vertical dividers wouldn't make
  // geometric sense). On smaller screens cells stack with row gaps instead.
  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {header}
      <div
        className="bg-white border border-[#E5E9EB] rounded-[24px]"
        style={{ padding: 24 }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-6 lg:gap-y-0 lg:h-24">
          {stats.map((stat, index) => (
            <div
              key={`card-${index}`}
              className={`relative flex flex-col items-center justify-center ${
                index < stats.length - 1
                  ? "lg:after:content-[''] lg:after:absolute lg:after:right-0 lg:after:top-1/2 lg:after:-translate-y-1/2 lg:after:h-[88px] lg:after:w-px lg:after:bg-[#E5E7E8]"
                  : ''
              }`}
              style={{ minWidth: 0, gap: 20 }}
            >
              <img
                src={stat.src}
                alt={stat.label}
                style={{ width: 24, height: 24 }}
              />
              <div
                className="flex flex-col items-center"
                style={{ gap: 4, width: '100%' }}
              >
                <div
                  className="font-normal text-center"
                  style={{
                    color: '#191B1C',
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: 24,
                    lineHeight: '32px',
                  }}
                >
                  {stat.value}
                </div>
                <div
                  className="flex items-center justify-center"
                  style={{
                    gap: 4,
                    color: '#9B919D',
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: 12,
                    lineHeight: '16px',
                    fontWeight: 400,
                  }}
                >
                  <span>{stat.label}</span>
                  {stat.tooltip ? (
                    <ToolTip text={stat.tooltip} position="top">
                      <InfoIcon />
                    </ToolTip>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CampaignOverViewComponent;
